import { supabase } from '@/integrations/supabase/client';

export interface LibraryAsset {
  id: string;
  name: string;
  assetType: string;
  kind: 'image' | 'gltf';
  storagePath: string;
  publicUrl: string;
  fallback2dUrl?: string;
  fileSizeBytes?: number;
  fileHash?: string;
}

export interface UploadResult {
  id: string;
  url: string;
  kind: 'image' | 'gltf';
  filename: string;
  storagePath: string;
}

const BUCKET_NAME = 'energyflow-assets';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Generate file hash for deduplication
async function generateFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Check if file already exists by hash
export async function checkDuplicate(file: File): Promise<LibraryAsset | null> {
  const hash = await generateFileHash(file);
  
  const { data, error } = await supabase
    .from('energyflow_library')
    .select('*')
    .eq('file_hash', hash)
    .maybeSingle();
  
  if (error || !data) return null;
  
  return {
    id: data.id,
    name: data.name,
    assetType: data.asset_type,
    kind: data.kind as 'image' | 'gltf',
    storagePath: data.storage_path,
    publicUrl: data.public_url,
    fallback2dUrl: data.fallback_2d_url,
    fileSizeBytes: data.file_size_bytes,
    fileHash: data.file_hash,
  };
}

// Upload asset to Supabase storage
export async function uploadAsset(
  file: File,
  assetType: string,
  name?: string
): Promise<UploadResult> {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`Bestand te groot. Maximum is 10MB, dit bestand is ${(file.size / 1024 / 1024).toFixed(1)}MB`);
  }
  
  // Determine kind based on file extension
  const isGLTF = file.name.match(/\.(glb|gltf)$/i);
  const kind: 'image' | 'gltf' = isGLTF ? 'gltf' : 'image';
  
  // Check for duplicates
  const existing = await checkDuplicate(file);
  if (existing) {
    console.log('[EnergyFlow] Duplicate found, reusing:', existing.publicUrl);
    return {
      id: existing.id,
      url: existing.publicUrl,
      kind: existing.kind,
      filename: existing.name,
      storagePath: existing.storagePath,
    };
  }
  
  // Generate unique filename
  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 11);
  const filename = `${timestamp}_${randomStr}.${ext}`;
  const storagePath = `shared/${assetType}/${filename}`;
  
  // Upload to Supabase Storage
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
    });
  
  if (error) {
    console.error('[EnergyFlow] Upload error:', error);
    throw new Error(`Upload mislukt: ${error.message}`);
  }
  
  // Get public URL
  const { data: urlData } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(storagePath);
  
  const publicUrl = urlData.publicUrl;
  
  // Generate file hash
  const fileHash = await generateFileHash(file);
  
  // Store in library database
  const { data: libraryEntry, error: dbError } = await supabase
    .from('energyflow_library')
    .insert({
      name: name || file.name,
      asset_type: assetType,
      kind,
      storage_path: storagePath,
      public_url: publicUrl,
      file_size_bytes: file.size,
      file_hash: fileHash,
      is_shared: true,
    })
    .select()
    .single();
  
  if (dbError) {
    console.error('[EnergyFlow] Database error:', dbError);
    // Clean up uploaded file if database insert fails
    await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
    throw new Error(`Database error: ${dbError.message}`);
  }
  
  return {
    id: libraryEntry.id,
    url: publicUrl,
    kind,
    filename: file.name,
    storagePath,
  };
}

// Delete asset from storage and database
export async function deleteAsset(id: string): Promise<void> {
  // Get the asset first to get storage path
  const { data: asset, error: fetchError } = await supabase
    .from('energyflow_library')
    .select('storage_path')
    .eq('id', id)
    .single();
  
  if (fetchError) {
    throw new Error(`Asset niet gevonden: ${fetchError.message}`);
  }
  
  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([asset.storage_path]);
  
  if (storageError) {
    console.warn('[EnergyFlow] Storage delete warning:', storageError);
  }
  
  // Delete from database
  const { error: dbError } = await supabase
    .from('energyflow_library')
    .delete()
    .eq('id', id);
  
  if (dbError) {
    throw new Error(`Database delete error: ${dbError.message}`);
  }
}

// List all assets in library
export async function listLibraryAssets(assetType?: string): Promise<LibraryAsset[]> {
  let query = supabase
    .from('energyflow_library')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (assetType) {
    query = query.eq('asset_type', assetType);
  }
  
  const { data, error } = await query;
  
  if (error) {
    console.error('[EnergyFlow] List error:', error);
    return [];
  }
  
  return data.map(item => ({
    id: item.id,
    name: item.name,
    assetType: item.asset_type,
    kind: item.kind as 'image' | 'gltf',
    storagePath: item.storage_path,
    publicUrl: item.public_url,
    fallback2dUrl: item.fallback_2d_url,
    fileSizeBytes: item.file_size_bytes,
    fileHash: item.file_hash,
  }));
}

// Set 2D fallback for a 3D asset
export async function set2DFallback(assetId: string, fallbackUrl: string): Promise<void> {
  const { error } = await supabase
    .from('energyflow_library')
    .update({ fallback_2d_url: fallbackUrl })
    .eq('id', assetId);
  
  if (error) {
    throw new Error(`Fallback update error: ${error.message}`);
  }
}

// Subscribe to library changes for real-time sync
export function subscribeToLibrary(
  callback: (assets: LibraryAsset[]) => void
): () => void {
  const channel = supabase
    .channel('energyflow-library-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'energyflow_library',
      },
      () => {
        // Refetch all assets when any change occurs
        listLibraryAssets().then(callback);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ============================================
// Scene Management
// ============================================

export interface SavedScene {
  id: string;
  name: string;
  description?: string;
  nodes: any[];
  connections: any[];
  settings: Record<string, any>;
  thumbnailUrl?: string;
  createdAt: string;
  updatedAt: string;
  isTemplate: boolean;
}

// Save scene to database
export async function saveScene(
  name: string,
  nodes: any[],
  connections: any[],
  settings: Record<string, any>,
  description?: string,
  thumbnailUrl?: string
): Promise<SavedScene> {
  const { data, error } = await supabase
    .from('energyflow_scenes')
    .insert({
      name,
      description,
      nodes,
      connections,
      settings,
      thumbnail_url: thumbnailUrl,
      is_template: false,
    })
    .select()
    .single();
  
  if (error) {
    throw new Error(`Scene opslaan mislukt: ${error.message}`);
  }
  
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    nodes: data.nodes as any[],
    connections: data.connections as any[],
    settings: data.settings as Record<string, any>,
    thumbnailUrl: data.thumbnail_url,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    isTemplate: data.is_template,
  };
}

// List saved scenes
export async function listScenes(): Promise<SavedScene[]> {
  const { data, error } = await supabase
    .from('energyflow_scenes')
    .select('*')
    .order('updated_at', { ascending: false });
  
  if (error) {
    console.error('[EnergyFlow] List scenes error:', error);
    return [];
  }
  
  return data.map(item => ({
    id: item.id,
    name: item.name,
    description: item.description,
    nodes: item.nodes as any[],
    connections: item.connections as any[],
    settings: item.settings as Record<string, any>,
    thumbnailUrl: item.thumbnail_url,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    isTemplate: item.is_template,
  }));
}

// Load a specific scene
export async function loadScene(sceneId: string): Promise<SavedScene | null> {
  const { data, error } = await supabase
    .from('energyflow_scenes')
    .select('*')
    .eq('id', sceneId)
    .single();
  
  if (error) {
    console.error('[EnergyFlow] Load scene error:', error);
    return null;
  }
  
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    nodes: data.nodes as any[],
    connections: data.connections as any[],
    settings: data.settings as Record<string, any>,
    thumbnailUrl: data.thumbnail_url,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    isTemplate: data.is_template,
  };
}

// Delete a scene
export async function deleteScene(sceneId: string): Promise<void> {
  const { error } = await supabase
    .from('energyflow_scenes')
    .delete()
    .eq('id', sceneId);
  
  if (error) {
    throw new Error(`Scene verwijderen mislukt: ${error.message}`);
  }
}

// Update scene
export async function updateScene(
  sceneId: string,
  updates: Partial<{
    name: string;
    description: string;
    nodes: any[];
    connections: any[];
    settings: Record<string, any>;
    thumbnailUrl: string;
  }>
): Promise<void> {
  const dbUpdates: Record<string, any> = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.nodes !== undefined) dbUpdates.nodes = updates.nodes;
  if (updates.connections !== undefined) dbUpdates.connections = updates.connections;
  if (updates.settings !== undefined) dbUpdates.settings = updates.settings;
  if (updates.thumbnailUrl !== undefined) dbUpdates.thumbnail_url = updates.thumbnailUrl;
  
  const { error } = await supabase
    .from('energyflow_scenes')
    .update(dbUpdates)
    .eq('id', sceneId);
  
  if (error) {
    throw new Error(`Scene updaten mislukt: ${error.message}`);
  }
}
